import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Shield, Plus, Trash2, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
    INCL: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "INCL" },
    BILLED: { bg: "bg-amber-50 text-amber-700 border-amber-200", label: "BILLED" },
    CLIENT: { bg: "bg-gray-50 text-gray-600 border-gray-200", label: "CLIENT" },
    NOT_IN_AGREEMENT: { bg: "bg-red-50 text-[#E02B2B] border-red-200", label: "NOT IN AGREEMENT" },
};

function GoldenBanner() {
    return (
        <div className="rounded-md bg-[#1E3355] text-white p-3 sm:p-4 flex items-start gap-3" data-testid="golden-rule-banner">
            <Shield className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#E02B2B]" />
            <div className="text-sm">
                <span className="font-display font-bold uppercase tracking-wider">Golden Rule</span> — If a service is <span className="font-semibold text-[#E02B2B]">not written in the client's agreement</span>, it is NOT included. Refer to Head Office first — every time, no exceptions.
            </div>
        </div>
    );
}

export default function SLALookupPage() {
    const { siteId: paramSiteId } = useParams();
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState(paramSiteId || null);
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        api.get("/sites").then(({ data }) => {
            // SLA only applies to MANAGED_CLIENT sites — hide the rest from the picker.
            const managed = data.filter((s) => (s.site_type || "MANAGED_CLIENT") === "MANAGED_CLIENT");
            setSites(managed);
            if (!siteId && managed.length) setSiteId(managed[0].id);
        });
    }, [siteId]);

    const load = useCallback(async () => {
        if (!siteId) return;
        setLoading(true);
        try { const { data } = await api.get(`/sla/${siteId}`); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId]);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return rows;
        return rows.filter((r) => r.service_name.toLowerCase().includes(s) || (r.meaning || "").toLowerCase().includes(s));
    }, [q, rows]);

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <GoldenBanner />
            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">SLA Lookup</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Client Service Matrix</h1>
                </div>
                <div className="flex items-center gap-2">
                    <select value={siteId || ""} onChange={(e) => setSiteId(e.target.value)} className="text-sm rounded-md border border-gray-300 bg-white px-3 py-2" data-testid="sla-site-select">
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search service…" className="pl-8 rounded-md border border-gray-300 px-3 py-2 text-sm bg-white" data-testid="sla-search" />
                    </div>
                </div>
            </div>

            <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                {loading ? <div className="p-6 text-center text-sm text-gray-500">Loading…</div> :
                filtered.length === 0 ? <div className="p-8 text-center text-sm text-gray-500" data-testid="sla-empty">No matching services.</div> :
                <ul className="divide-y divide-gray-100" data-testid="sla-list">
                    {filtered.map((r) => (
                        <li key={r.id} className="p-4 sm:p-5 hover:bg-[#FAFBFC] cursor-pointer" onClick={() => setDetail(r)} data-testid={`sla-row-${r.service_name}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-display font-semibold text-[#1E3355]">{r.service_name}</div>
                                    {r.meaning && <div className="mt-0.5 text-xs text-gray-500 line-clamp-1">{r.meaning}</div>}
                                </div>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold border ${STATUS_STYLE[r.status]?.bg || ""}`}>{STATUS_STYLE[r.status]?.label || r.status}</span>
                            </div>
                        </li>
                    ))}
                </ul>}
            </div>

            {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function DetailModal({ row, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="sla-detail-modal">
            <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className="p-5">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold border ${STATUS_STYLE[row.status]?.bg || ""}`}>{STATUS_STYLE[row.status]?.label || row.status}</span>
                    <div className="font-display font-bold text-lg text-[#1E3355] mt-2">{row.service_name}</div>
                    {row.meaning && <div className="mt-2 text-sm text-gray-800"><span className="font-semibold">Meaning: </span>{row.meaning}</div>}
                    {row.client_note && <div className="mt-2 text-sm text-gray-800"><span className="font-semibold">Client-specific note: </span>{row.client_note}</div>}
                    {row.watch_outs && <div className="mt-2 text-sm text-[#E02B2B]"><span className="font-semibold">Watch-outs: </span>{row.watch_outs}</div>}
                    <div className="mt-4 p-3 bg-[#F4F6F9] rounded-md text-[11px] text-[#1E3355]">
                        Reminder: if it's not written in the agreement, refer to HO first.
                    </div>
                </div>
                <button onClick={onClose} className="w-full py-3 border-t border-gray-100 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="sla-detail-close">Close</button>
            </div>
        </div>
    );
}

export function SLAEditorPage() {
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ service_name: "", status: "INCL", meaning: "", client_note: "", watch_outs: "" });
    const [showBulk, setShowBulk] = useState(false);

    useEffect(() => { api.get("/sites").then(({ data }) => { setSites(data); if (!siteId && data.length) setSiteId(data[0].id); }); }, [siteId]);
    const load = useCallback(async () => {
        if (!siteId) return;
        setLoading(true);
        try { const { data } = await api.get(`/sla/${siteId}`); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId]);
    useEffect(() => { load(); }, [load]);

    async function add(e) {
        e.preventDefault();
        try {
            await api.post("/sla", { ...form, site_id: siteId });
            toast.success("Row added");
            setForm({ service_name: "", status: "INCL", meaning: "", client_note: "", watch_outs: "" });
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function update(id, patch) { try { await api.patch(`/sla/${id}`, patch); load(); } catch (e) { toast.error(apiError(e)); } }
    async function del(id) { try { await api.delete(`/sla/${id}`); load(); } catch (e) { toast.error(apiError(e)); } }

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <GoldenBanner />
            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">SLA Matrix Editor</h1>
                </div>
                <div className="flex items-center gap-2">
                    <select value={siteId || ""} onChange={(e) => setSiteId(e.target.value)} className="text-sm rounded-md border border-gray-300 bg-white px-3 py-2" data-testid="sla-editor-site-select">
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button onClick={() => setShowBulk(true)} className="inline-flex items-center gap-1 rounded-md border border-[#1E3355] text-[#1E3355] hover:bg-[#F4F6F9] text-sm font-semibold px-3 py-2" data-testid="sla-bulk-open"><Upload className="w-4 h-4" /> Bulk import</button>
                </div>
            </div>

            <form onSubmit={add} className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="sla-add-form">
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                    <input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} placeholder="Service name" required className={fieldCls + " sm:col-span-2"} data-testid="sla-add-name" />
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={fieldCls} data-testid="sla-add-status">
                        {Object.keys(STATUS_STYLE).map((k) => <option key={k} value={k}>{STATUS_STYLE[k].label}</option>)}
                    </select>
                    <input value={form.meaning} onChange={(e) => setForm({ ...form, meaning: e.target.value })} placeholder="Meaning" className={fieldCls + " sm:col-span-2"} />
                    <button type="submit" className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="sla-add-submit"><Plus className="w-4 h-4" /> Add</button>
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={form.client_note} onChange={(e) => setForm({ ...form, client_note: e.target.value })} placeholder="Client-specific note" className={fieldCls} />
                    <input value={form.watch_outs} onChange={(e) => setForm({ ...form, watch_outs: e.target.value })} placeholder="Watch-outs" className={fieldCls} />
                </div>
            </form>

            <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <table className="w-full text-sm" data-testid="sla-editor-table">
                    <thead className="bg-[#F4F6F9]">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            <th className="px-3 py-2">Service</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Meaning</th>
                            <th className="px-3 py-2">Client note</th>
                            <th className="px-3 py-2">Watch-outs</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? <tr><td colSpan="6" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr> :
                        rows.map((r) => (
                            <tr key={r.id} data-testid={`sla-editor-row-${r.service_name}`}>
                                <td className="px-3 py-2"><input defaultValue={r.service_name} onBlur={(e) => e.target.value !== r.service_name && update(r.id, { service_name: e.target.value })} className="text-sm rounded border-transparent hover:border-gray-200 focus:border-[#1E3355] px-2 py-1 bg-transparent focus:outline-none focus:ring-1 focus:ring-[#1E3355] w-full" /></td>
                                <td className="px-3 py-2"><select defaultValue={r.status} onChange={(e) => update(r.id, { status: e.target.value })} className="text-xs rounded border border-gray-200 px-2 py-1">{Object.keys(STATUS_STYLE).map((k) => <option key={k} value={k}>{k}</option>)}</select></td>
                                <td className="px-3 py-2"><input defaultValue={r.meaning || ""} onBlur={(e) => update(r.id, { meaning: e.target.value })} className="text-xs w-full rounded border-transparent hover:border-gray-200 px-2 py-1" /></td>
                                <td className="px-3 py-2"><input defaultValue={r.client_note || ""} onBlur={(e) => update(r.id, { client_note: e.target.value })} className="text-xs w-full rounded border-transparent hover:border-gray-200 px-2 py-1" /></td>
                                <td className="px-3 py-2"><input defaultValue={r.watch_outs || ""} onBlur={(e) => update(r.id, { watch_outs: e.target.value })} className="text-xs w-full rounded border-transparent hover:border-gray-200 px-2 py-1" /></td>
                                <td className="px-3 py-2 text-right"><button onClick={() => del(r.id)} className="p-1 text-gray-400 hover:text-[#E02B2B]" data-testid={`sla-delete-${r.service_name}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showBulk && <BulkImportModal siteId={siteId} siteName={sites.find((s) => s.id === siteId)?.name} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); load(); }} />}
        </div>
    );
}

function BulkImportModal({ siteId, siteName, onClose, onDone }) {
    const [format, setFormat] = useState("csv");
    const [text, setText] = useState(
        "service_name,status,meaning,client_note,watch_outs\nHousekeeping daily,INCL,Contracted hours,,Verify uniform"
    );
    const [replace, setReplace] = useState(false);
    const [preview, setPreview] = useState(null); // {rows, errors}
    const [busy, setBusy] = useState(false);

    function parse() {
        const errors = [];
        const rows = [];
        try {
            if (format === "json") {
                const raw = JSON.parse(text);
                if (!Array.isArray(raw)) throw new Error("JSON must be an array of objects");
                raw.forEach((r, i) => {
                    if (!r.service_name || !r.status) errors.push(`Row ${i + 1}: missing service_name or status`);
                    else rows.push({
                        service_name: String(r.service_name).trim(),
                        status: String(r.status).trim().toUpperCase(),
                        meaning: r.meaning ? String(r.meaning).trim() : "",
                        client_note: r.client_note ? String(r.client_note).trim() : "",
                        watch_outs: r.watch_outs ? String(r.watch_outs).trim() : "",
                    });
                });
            } else {
                const lines = text.split(/\r?\n/).filter((l) => l.trim());
                if (lines.length < 2) throw new Error("CSV needs a header row and at least one data row");
                const headers = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
                const idx = {
                    service_name: headers.indexOf("service_name"),
                    status: headers.indexOf("status"),
                    meaning: headers.indexOf("meaning"),
                    client_note: headers.indexOf("client_note"),
                    watch_outs: headers.indexOf("watch_outs"),
                };
                if (idx.service_name < 0 || idx.status < 0) throw new Error("CSV header must include service_name and status");
                for (let i = 1; i < lines.length; i++) {
                    const cells = splitCsvLine(lines[i]);
                    const service_name = (cells[idx.service_name] || "").trim();
                    const status = (cells[idx.status] || "").trim().toUpperCase();
                    if (!service_name || !status) { errors.push(`Row ${i + 1}: missing service_name or status`); continue; }
                    rows.push({
                        service_name,
                        status,
                        meaning: idx.meaning >= 0 ? (cells[idx.meaning] || "").trim() : "",
                        client_note: idx.client_note >= 0 ? (cells[idx.client_note] || "").trim() : "",
                        watch_outs: idx.watch_outs >= 0 ? (cells[idx.watch_outs] || "").trim() : "",
                    });
                }
            }
            const validStatuses = ["INCL", "BILLED", "CLIENT", "NOT_IN_AGREEMENT"];
            rows.forEach((r, i) => { if (!validStatuses.includes(r.status)) errors.push(`Row ${i + 1} (${r.service_name}): invalid status '${r.status}'`); });
        } catch (e) { errors.push(e.message); }
        setPreview({ rows, errors });
    }

    async function submit() {
        if (!preview || preview.errors.length > 0 || preview.rows.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/sla/bulk-import", { site_id: siteId, rows: preview.rows, replace_existing: replace });
            toast.success(`Imported ${data.inserted} rows${replace ? " (replaced existing)" : ""}`);
            onDone();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="sla-bulk-modal">
            <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Bulk import</div>
                    <div className="font-display font-semibold text-[#1E3355] mt-0.5">SLA rows for {siteName || "site"}</div>
                    <div className="text-xs text-gray-500 mt-1">Paste CSV or JSON below. Valid statuses: INCL, BILLED, CLIENT, NOT_IN_AGREEMENT.</div>
                </div>
                <div className="p-5 space-y-3">
                    <div className="flex items-center gap-3">
                        <label className="text-xs text-gray-600 inline-flex items-center gap-1"><input type="radio" checked={format === "csv"} onChange={() => setFormat("csv")} data-testid="sla-bulk-format-csv" /> CSV</label>
                        <label className="text-xs text-gray-600 inline-flex items-center gap-1"><input type="radio" checked={format === "json"} onChange={() => setFormat("json")} data-testid="sla-bulk-format-json" /> JSON</label>
                        <label className="text-xs text-gray-600 inline-flex items-center gap-1 ml-auto"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="sla-bulk-replace" /> Replace existing rows</label>
                    </div>
                    <textarea value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }} rows={8} className="w-full font-mono text-xs rounded border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-[#1E3355]" data-testid="sla-bulk-text" />
                    <div className="flex gap-2">
                        <button onClick={parse} className="rounded-md border border-[#1E3355] text-[#1E3355] hover:bg-[#F4F6F9] text-sm font-semibold px-4 py-2" data-testid="sla-bulk-preview">Preview</button>
                        {preview && preview.errors.length === 0 && preview.rows.length > 0 && (
                            <button onClick={submit} disabled={busy} className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-60" data-testid="sla-bulk-confirm">
                                Import {preview.rows.length} row{preview.rows.length > 1 ? "s" : ""}
                            </button>
                        )}
                    </div>
                    {preview && (
                        <div className="rounded-md border border-gray-200 p-3 bg-[#FAFBFC]" data-testid="sla-bulk-preview-panel">
                            {preview.errors.length > 0 && (
                                <div className="text-xs text-[#E02B2B] mb-2">
                                    <div className="font-semibold">Errors ({preview.errors.length}):</div>
                                    <ul className="mt-1 list-disc list-inside">{preview.errors.map((e, i) => <li key={`${i}-${e}`}>{e}</li>)}</ul>
                                </div>
                            )}
                            {preview.rows.length > 0 && (
                                <div className="text-xs text-gray-700">
                                    <div className="font-semibold mb-1">Preview ({preview.rows.length} rows):</div>
                                    <ul className="space-y-0.5 max-h-40 overflow-y-auto">{preview.rows.map((r, i) => (
                                        <li key={`${i}-${r.service_name}`}><span className="font-semibold text-[#1E3355]">{r.service_name}</span> <span className={`inline-block ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${STATUS_STYLE[r.status]?.bg || ""}`}>{r.status}</span>{r.meaning ? ` — ${r.meaning}` : ""}</li>
                                    ))}</ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <button onClick={onClose} className="w-full py-3 border-t border-gray-100 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="sla-bulk-close">Close</button>
            </div>
        </div>
    );
}

function splitCsvLine(line) {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (inQ) {
            if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
            else if (c === '"') inQ = false;
            else cur += c;
        } else {
            if (c === '"') inQ = true;
            else if (c === ",") { out.push(cur); cur = ""; }
            else cur += c;
        }
    }
    out.push(cur);
    return out;
}

const fieldCls = "rounded-md border border-gray-300 px-3 py-2 text-sm bg-white";
